import type { ReactElement } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Ban, SearchX } from "lucide-react";
import { Button, EmptyState } from "@playbookai/ui";
import { MODULE_DISPLAY_NAMES } from "@playbookai/api-clients";
import { useAuthStore } from "../auth/auth-store.js";
import { SETTINGS_KPI_ROUTE } from "../routes.js";
import { ALL_MODULES, getEnabledModules } from "./index.js";
import { isModuleAvailable, moduleUnavailableNotice } from "./availability.js";

/**
 * Safe state for a route that maps to no enabled module (PB-34, SH-05 AC2).
 *
 * Mounted at `path="*"` in `App.tsx` as the lowest-priority route, so it catches
 * a direct URL to a *disabled* module — whose `routeBase/*` route is never
 * registered when the module is off — as well as any otherwise-unknown path. A
 * disabled-module URL gets module-aware copy (matched by route-base prefix, so
 * deep links like `/app/vsm/<id>` keep it); anything else gets a generic
 * "page not found". Either way the user lands on an in-shell empty state with a
 * way home, never a silent redirect.
 *
 * Static import (no lazy chunk): a disabled module's bundle is never loaded by
 * this path. The host supplies the domain wording; the DS `EmptyState` molecule
 * does the rendering — same split as `NoModulesEmptyState`.
 */
export function NotFoundRoute(): ReactElement {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const tenantEnabledModules = useAuthStore(
    (state) => state.claims?.enabledModules,
  );

  const enabledIds = new Set(
    getEnabledModules(tenantEnabledModules).map((module) => module.id),
  );
  /* Two module pages live outside a module's route base (ADR-0100): KPI
     Manager in Settings, and Hoshin's Target to Improve page. */
  const disabledModule = [
    ...ALL_MODULES.map((m) => ({
      id: m.id as string,
      label: m.label,
      routeBase: m.routeBase,
      off: !enabledIds.has(m.id),
    })),
    {
      id: "kpi",
      label: MODULE_DISPLAY_NAMES.kpi,
      routeBase: SETTINGS_KPI_ROUTE,
      off: !isModuleAvailable("kpi", tenantEnabledModules),
    },
    {
      id: "hoshin",
      label: MODULE_DISPLAY_NAMES.hoshin,
      routeBase: "/app/tti",
      off: !isModuleAvailable("hoshin", tenantEnabledModules),
    },
  ].find(
    (m) =>
      m.off &&
      (pathname === m.routeBase || pathname.startsWith(`${m.routeBase}/`)),
  );

  const title = disabledModule
    ? `${disabledModule.label} isn’t available`
    : "Page not found";
  // The one wording pair (d7): turned off by an administrator, or not part of
  // this deployment.
  const description = disabledModule
    ? `${moduleUnavailableNotice(disabledModule.id, disabledModule.label, tenantEnabledModules)}. Contact your admin if you think it should be.`
    : "We couldn’t find the page you’re looking for. It may have moved, or the link may be incorrect.";

  return (
    <div className="pb-motion-content-enter mx-auto max-w-6xl px-6 py-12">
      <EmptyState
        variant="no-results"
        icon={
          disabledModule ? (
            <Ban className="w-5 h-5" />
          ) : (
            <SearchX className="w-5 h-5" />
          )
        }
        title={title}
        description={description}
        action={
          <Button variant="secondary" onClick={() => navigate("/")}>
            Go to home
          </Button>
        }
      />
    </div>
  );
}
